import React from "react";
import { Icon, Popover } from "@amzn/awsui-components-console";

/**
 * FUSEN Execution Mode — how FUSEN WF should resolve the effective event version
 * for a FUSENWorkflow redrive. Only applies to FUSENWorkflow redrives.
 */
export const FUSEN_EXECUTION_MODE_NORMAL = "NORMAL";
export const FUSEN_EXECUTION_MODE_REDRIVE = "REDRIVE";
export const FUSEN_EXECUTION_MODE_REDRIVE_WITH_HIGHER_VERSION =
  "REDRIVE_WITH_HIGHER_VERSION";

export const FUSEN_WORKFLOW_TYPE = "FUSENWorkflow";

export const FUSEN_EXECUTION_MODE_LABEL = "FUSEN Execution Mode";

export const FUSEN_EXECUTION_MODE_VALUES = [
  FUSEN_EXECUTION_MODE_NORMAL,
  FUSEN_EXECUTION_MODE_REDRIVE,
  FUSEN_EXECUTION_MODE_REDRIVE_WITH_HIGHER_VERSION,
];

export const FUSEN_EXECUTION_MODE_OPTIONS = FUSEN_EXECUTION_MODE_VALUES.map(
  (value) => ({ value, label: value }),
);

/**
 * The event version override is only meaningful in NORMAL mode; in the two redrive
 * modes FUSEN WF derives the version from what it finds in record.
 */
export function isEventVersionOverrideDisabled(
  fusenExecutionMode?: string | null,
): boolean {
  return (
    fusenExecutionMode === FUSEN_EXECUTION_MODE_REDRIVE ||
    fusenExecutionMode === FUSEN_EXECUTION_MODE_REDRIVE_WITH_HIGHER_VERSION
  );
}

export const EVENT_VERSION_OVERRIDE_DISABLED_REASON =
  `Not used in ${FUSEN_EXECUTION_MODE_REDRIVE} or ` +
  `${FUSEN_EXECUTION_MODE_REDRIVE_WITH_HIGHER_VERSION} mode — FUSEN workflow derives ` +
  `the event version from what is already in record.`;

/**
 * The mode to show for a redrive row. An absent value means the record predates the field, and
 * absent is NORMAL downstream, so it displays as NORMAL. Workflow types other than FUSENWorkflow
 * never carry a mode, so they render blank rather than a misleading NORMAL.
 */
export function displayFusenExecutionMode(
  workflowType?: string | null,
  fusenExecutionMode?: string | null,
): string {
  if (workflowType !== FUSEN_WORKFLOW_TYPE) {
    return "";
  }
  return fusenExecutionMode || FUSEN_EXECUTION_MODE_NORMAL;
}

/** Help icon with a popover explaining each FUSEN Execution Mode. */
export const FusenExecutionModeHelp = () => (
  <Popover
    renderWithPortal
    size="medium"
    position="top"
    triggerType="text"
    dismissButton={false}
    header={FUSEN_EXECUTION_MODE_LABEL}
    content={
      <ul style={{ paddingLeft: "1rem", margin: 0 }}>
        <li>
          <strong>NORMAL</strong>: use the specified event version override
        </li>
        <li>
          <strong>REDRIVE</strong>: use the same version as the largest FUSEN
          version found in record, or 0 if none is found (recommended for
          transient issue)
        </li>
        <li>
          <strong>REDRIVE_WITH_HIGHER_VERSION</strong>: use the largest FUSEN
          version found in record + 1, or 0 if none is found (recommended for
          transaction correction)
        </li>
      </ul>
    }
  >
    <Icon name="status-info" variant="link" />
  </Popover>
);

/**
 * The label plus help icon, for places that are not a FormField. A FormField should pass the
 * label as `label` and {@link FusenExecutionModeHelp} as `info` instead, so Cloudscape handles
 * the alignment of the icon against the label itself.
 */
export const FusenExecutionModeLabel = ({
  className,
}: {
  className?: string;
}) => (
  <span
    className={className}
    style={{
      display: "inline-flex",
      alignItems: "center",
      columnGap: "0.25rem",
    }}
  >
    {FUSEN_EXECUTION_MODE_LABEL}
    <FusenExecutionModeHelp />
  </span>
);
